/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { api, ApiError } from "core/api";
import { Button, Card, Input, Form, FieldGroup, Select, Switch } from "core/components";
import { navigateTo } from "core/navigation";
import {
  InventoryItemFormData,
  InventoryItemSchema,
  InventoryTypeMask,
  InventoryTypeMaxLength,
  ItemCategoryFormData,
  ItemSubcategoryFormData,
  SupplierFormData,
  UnitMeasureFormData
} from "core/schema";
import { admin_inventory_items_path, admin_inventory_item_path } from "routes";
import { IMaskInput } from "react-imask";
import InventoryItemSuppliersForm from "./InventoryItemSuppliersForm";
import ItemPriceHistory from "./ItemPriceHistory";

type InventoryItemFormProps = {
  inventory_item: InventoryItemFormData;
  item_classifications: Array<Record<string, unknown>>;
  item_categories: Array<ItemCategoryFormData>;
  item_subcategories: Array<ItemSubcategoryFormData>;
  unit_measures: Array<UnitMeasureFormData>;
  suppliers: Array<SupplierFormData>;
  credit_terms: Array<Record<string, unknown>>;
};

const selectGroupStyle: CSSProperties = {
  flex: 1,
  fontSize: 14,
  fontWeight: "bold",
  lineHeight: "1",
  color: "#172B4D",
  display: "inline-block",
  textAlign: "center"
};

enum InventoryItemFormTabs {
  InventoryItem = 0,
  Suppliers = 1,
  PriceHistory = 2
}

const InventoryItemForm = ({
  inventory_item,
  item_classifications,
  item_categories,
  item_subcategories,
  unit_measures,
  suppliers,
  credit_terms
}: InventoryItemFormProps): JSX.Element => {
  const formMethods = useForm<InventoryItemFormData>({
    mode: "onChange",
    defaultValues: InventoryItemSchema.cast(inventory_item),
    resolver: yupResolver(InventoryItemSchema)
  });
  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting, errors }
  } = formMethods;
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);
  const [selectedTab, setSelectedTab] = React.useState(InventoryItemFormTabs.InventoryItem);
  const groupedItemClassifications = item_classifications?.map((item_classification) => {
    return { label: `${item_classification.code} - ${item_classification.name}`, options: item_classification.item_categories };
  });
  const isStockable = watch("stockable");
  const [inventoryItemSuppliers, setInventoryItemSuppliers] = React.useState(inventory_item.inventory_item_suppliers);

  const save = async (data: InventoryItemFormData): Promise<unknown> => {
    try {
      if (data.id) {
        await api.put(admin_inventory_item_path(inventory_item), data);
      } else {
        await api.post(admin_inventory_items_path(inventory_item), data);
      }

      navigateTo(admin_inventory_items_path({ item_type: inventory_item.item_type }));
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  const formatGroupLabel = (data) => {
    return (
      <div className="d-flex justify-content-between mb-2" style={selectGroupStyle}>
        <span>{data.label}</span>
        <span>{data.options.length}</span>
      </div>
    );
  };

  return (
    <React.StrictMode>
      <FormProvider {...formMethods}>
        <Form errors={formErrors}>
          <Tabs
            className="card"
            selectedTabClassName="project-tab__link project-tab__link--active"
            onSelect={(index) => {
              setSelectedTab(index);
            }}
          >
            <Container fluid={true}>
              <Row className="d-flex align-items-baseline">
                <Col>
                  <TabList className="card__header" style={{ padding: 0 }}>
                    <Col className="d-flex justify-content-start">
                      <Tab className="project-tab__link">Inventory Item</Tab>
                      {inventory_item.id && (
                        <>
                          <Tab className="project-tab__link">Suppliers</Tab>
                          <Tab className="project-tab__link">Price History</Tab>
                        </>
                      )}
                    </Col>
                  </TabList>
                </Col>
                <Col className="d-flex justify-content-end">
                  <Button
                    icon="arrow-left"
                    iconProps={{ className: "mr-3" }}
                    className="button--default mr-3"
                    disabled={isSubmitting}
                    title="Back"
                    onClick={() => navigateTo(admin_inventory_items_path({ item_type: inventory_item.item_type }))}
                  />
                  {selectedTab == InventoryItemFormTabs.InventoryItem && (
                    <Button
                      icon="save"
                      iconProps={{ className: "mr-3" }}
                      className="button--brand mr-3"
                      disabled={isSubmitting}
                      title="Save"
                      onClick={handleSubmit(async (data) => await save(data))}
                    />
                  )}
                </Col>
              </Row>
            </Container>
            <TabPanel>
              <Card.Body>
                <Container fluid={true}>
                  <Row className="px-3">
                    <Col sm={1}>
                      <Controller
                        name="active"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            {...field}
                            label="Active"
                            info="Active or Inactive"
                            checked={field.value}
                            value={field.value?.toString()}
                            errorMessage={errors.active?.message}
                          />
                        )}
                      />
                    </Col>
                    <Col sm={1}>
                      <Controller
                        name="stockable"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            {...field}
                            label="Stockable"
                            info="Stockable"
                            checked={field.value}
                            value={field.value?.toString()}
                            errorMessage={errors.stockable?.message}
                          />
                        )}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col lg={6} className="px-4">
                      <Controller
                        name="item_category_id"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            label="Inventory Type"
                            placeholder="Select Inventory Type"
                            required
                            options={groupedItemClassifications}
                            formatGroupLabel={(option) => formatGroupLabel(option)}
                            getOptionLabel={(option) => `${option.code} - ${option.name}`}
                            getOptionValue={(option) => option.id}
                            isOptionDisabled={(option) => !option.active}
                            onChange={(newOption) => {
                              field.onChange(newOption?.id);
                              setValue("item_subcategory_id", null);
                            }}
                            value={item_categories.find((item_category) => item_category.id == field.value) || null}
                            errorMessage={errors.item_category_id?.message}
                          />
                        )}
                      />
                      <Controller
                        name="item_subcategory_id"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            label="Inventory Subtype"
                            placeholder="Select Inventory Subtype"
                            noOptionsMessage={() => "Select an Inventory Type first."}
                            required
                            options={item_subcategories?.filter((item_subcategory) => item_subcategory.item_category_id == watch("item_category_id"))}
                            getOptionLabel={(option) => `${option.code} - ${option.name}`}
                            getOptionValue={(option) => option.id}
                            isOptionDisabled={(option) => !option.active}
                            onChange={(newOption) => {
                              field.onChange(newOption?.id);
                            }}
                            value={item_subcategories.find((item_subcategory) => item_subcategory.id == field.value) || null}
                            errorMessage={errors.item_subcategory_id?.message}
                          />
                        )}
                      />
                      <Controller
                        name="code"
                        control={control}
                        render={({ field }) => (
                          <FieldGroup label="Code" required errorMessage={errors.code?.message}>
                            <IMaskInput
                              mask={InventoryTypeMask}
                              value={field.value}
                              unmask={false}
                              inputRef={field.ref}
                              onAccept={(value) => {
                                field.onChange(value);
                              }}
                              maxLength={InventoryTypeMaxLength}
                              className="field text-uppercase"
                              placeholder="1000000"
                            />
                          </FieldGroup>
                        )}
                      />
                    </Col>
                    <Col lg={6} className="px-4">
                      <Input {...register("name")} label="Name" required errorMessage={errors.name?.message} placeholder="Labor" maxLength={100} />
                      <Row>
                        <Col>
                          <Controller
                            name="unit_measure_id"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                label="Unit of Measure"
                                placeholder="Select Unit of Measure"
                                required
                                options={unit_measures}
                                getOptionLabel={(option) => `${option.name} (${option.code})`}
                                getOptionValue={(option) => option.id}
                                isOptionDisabled={(option) => !option.active}
                                onChange={(newOption) => {
                                  field.onChange(newOption?.id);
                                }}
                                value={unit_measures?.find((unit_measure) => unit_measure.id == field.value) || null}
                                errorMessage={errors.unit_measure_id?.message}
                              />
                            )}
                          />
                        </Col>
                        <Col>
                          <Controller
                            name="standard_price"
                            control={control}
                            render={({ field }) => (
                              <FieldGroup label="Standard Price" required errorMessage={errors.standard_price?.message}>
                                <div className="d-flex">
                                  <span
                                    className="field"
                                    style={{
                                      flex: 1,
                                      alignItems: "center",
                                      fontWeight: "bold",
                                      backgroundColor: "#e9ecef",
                                      border: "1px solid #ced4da",
                                      whiteSpace: "nowrap",
                                      paddingTop: "10px",
                                      borderTopRightRadius: 0,
                                      borderBottomRightRadius: 0
                                    }}
                                  >
                                    ₱
                                  </span>
                                  <IMaskInput
                                    mask={Number}
                                    padFractionalZeros={true}
                                    thousandsSeparator=","
                                    radix="."
                                    value={field.value?.toString()}
                                    unmask={true}
                                    inputRef={field.ref}
                                    onAccept={(value) => {
                                      field.onChange(value);
                                    }}
                                    className="field"
                                    placeholder="1000.00"
                                    min={0}
                                    max={9999999}
                                  />
                                </div>
                              </FieldGroup>
                            )}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Controller
                            name="reorder_point"
                            control={control}
                            render={({ field }) => (
                              <FieldGroup label="Reorder Point" required={isStockable} errorMessage={errors.reorder_point?.message}>
                                <IMaskInput
                                  mask={Number}
                                  radix="."
                                  value={field.value?.toString()}
                                  unmask={true}
                                  inputRef={field.ref}
                                  onAccept={(value) => {
                                    field.onChange(value);
                                  }}
                                  className="field text-uppercase"
                                  placeholder="100"
                                  maxLength="13"
                                />
                              </FieldGroup>
                            )}
                          />
                        </Col>
                        <Col>
                          <Controller
                            name="safety_stock_level"
                            control={control}
                            render={({ field }) => (
                              <FieldGroup label="Safety Stock Level" required={isStockable} errorMessage={errors.safety_stock_level?.message}>
                                <IMaskInput
                                  mask={Number}
                                  radix="."
                                  value={field.value?.toString()}
                                  unmask={true}
                                  inputRef={field.ref}
                                  onAccept={(value) => {
                                    field.onChange(value);
                                  }}
                                  className="field text-uppercase"
                                  placeholder="5"
                                  maxLength="13"
                                />
                              </FieldGroup>
                            )}
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Container>
              </Card.Body>
            </TabPanel>
            {inventory_item.id && (
              <>
                <TabPanel>
                  <InventoryItemSuppliersForm
                    inventory_item={inventory_item}
                    inventoryItemSuppliers={inventoryItemSuppliers}
                    setInventoryItemSuppliers={setInventoryItemSuppliers}
                    suppliers={suppliers}
                    credit_terms={credit_terms}
                  />
                </TabPanel>
                <TabPanel>
                  <ItemPriceHistory inventory_item={inventory_item} />
                </TabPanel>
              </>
            )}
          </Tabs>
        </Form>
      </FormProvider>
    </React.StrictMode>
  );
};

export default InventoryItemForm;
